/**
 * CORE-1683 — one-line context for a quoted message: what the reply banner in
 * the composer and the quote block above a reply show. Text wins; a message of
 * attachments only is named by what it carries, so a reply to a photo reads
 * «Фото», not an empty line. No React — checked in node by jest.
 */
import { mentionsToPlainText } from './chatMentions';
import { emojiTokensToPlainText } from './emojiTokens';

function attachmentKind(a: any): 'image' | 'video' | 'voice' | 'file' {
  const type = String(a?.type ?? '');
  if (type === 'image') return 'image';
  if (type === 'video' || type === 'og:video') return 'video';
  if (type === 'voiceRecording' || type === 'audio') return 'voice';
  return 'file';
}

/** «Фото», «Фото (3)», «Видео», «Голосовое сообщение», «Файл: отчёт.pdf»; null when there is nothing. */
export function describeAttachments(attachments: readonly any[] | null | undefined): string | null {
  const list = (attachments ?? []).filter(Boolean);
  if (list.length === 0) return null;
  const kinds = list.map(attachmentKind);
  const first = kinds[0];
  const uniform = kinds.every((k) => k === first);
  if (!uniform) return `Вложения (${list.length})`;
  const count = list.length > 1 ? ` (${list.length})` : '';
  switch (first) {
    case 'image': return `Фото${count}`;
    case 'video': return `Видео${count}`;
    case 'voice': return `Голосовое сообщение${count}`;
    default: {
      if (list.length > 1) return `Файлы (${list.length})`;
      const a = list[0];
      const name = a?.fileName ?? a?.title ?? a?.name;
      return name ? `Файл: ${name}` : 'Файл';
    }
  }
}

/**
 * The quote line with the kit's emoji tokens kept as tokens — for places that
 * draw them as pictures through `EmojiText` (CORE-1685, QA 16.10: the system
 * ❤️ in a preview next to the kit's picture in the message read as a mismatch).
 */
export function replyPreviewSource(text: string | null | undefined, attachments?: readonly any[] | null): string {
  const source = mentionsToPlainText(text ?? '').trim();
  if (source) return source;
  return describeAttachments(attachments) ?? '';
}

/** The quote line as plain text: kit emoji as fallback symbols, for places without pictures. */
export function replyPreviewText(text: string | null | undefined, attachments?: readonly any[] | null): string {
  return emojiTokensToPlainText(replyPreviewSource(text, attachments));
}
